import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react';
import './ImageUpload.css';

/**
 * ImageUpload Component
 *
 * A reusable image upload component with preview, validation, and delete functionality
 *
 * @param {Object} props
 * @param {string} props.label - Label text for the upload area
 * @param {string} props.currentImageUrl - URL of the currently uploaded image
 * @param {Function} props.onUpload - Callback when file is selected (receives File object)
 * @param {Function} props.onDelete - Callback when delete is clicked
 * @param {boolean} props.isUploading - Whether upload is in progress
 * @param {string} props.maxSize - Max file size text (e.g., "100KB", "1MB")
 * @param {string} props.dimensions - Recommended dimensions text (e.g., "500x500px")
 * @param {Array} props.acceptedFormats - Array of accepted mime types
 * @param {number} props.maxSizeBytes - Max file size in bytes
 */
const ImageUpload = ({
  label = 'Upload Image',
  currentImageUrl = null,
  onUpload,
  onDelete,
  isUploading = false,
  maxSize = '1MB',
  dimensions = '',
  acceptedFormats = ['image/jpeg', 'image/png', 'image/webp'],
  maxSizeBytes = 1048576, // 1MB default
}) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return `Invalid file type. Accepted formats: ${acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}`;
    }

    // Check file size
    if (file.size > maxSizeBytes) {
      return `File too large. Maximum size: ${maxSize}`;
    }

    return null;
  };

  const handleFileSelect = (file) => {
    setError(null);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Call upload callback
    if (onUpload) {
      onUpload(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDelete = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onDelete) {
      onDelete();
    }
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const displayImage = preview || currentImageUrl;

  return (
    <div className="image-upload-container">
      <label className="image-upload-label">{label}</label>

      <div
        className={`image-upload-area ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleFileInputChange}
          className="image-upload-input"
          disabled={isUploading}
        />

        {isUploading ? (
          <div className="image-upload-loading">
            <Loader size={32} className="spinner" />
            <p>Uploading...</p>
          </div>
        ) : displayImage ? (
          <div className="image-upload-preview">
            <img src={displayImage} alt="Preview" className="preview-image" />
            <button
              type="button"
              className="image-delete-button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              aria-label="Delete image"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="image-upload-placeholder">
            <ImageIcon size={32} />
            <p className="upload-text">
              <Upload size={16} />
              <span>Click to upload or drag and drop</span>
            </p>
            {dimensions && <p className="upload-hint">{dimensions}</p>}
            <p className="upload-hint">Max size: {maxSize}</p>
          </div>
        )}
      </div>

      {error && (
        <div className="image-upload-error">
          <X size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
